/**
 * HPBar 컴포넌트
 * 레트로 게임 스타일의 HP/진행도 바
 * - 도트 패턴 배경
 * - 단계별 색상 변화
 * - 픽셀 애니메이션
 */

import React from 'react';

/**
 * HPBar Props
 */
interface HPBarProps {
  /**
   * 현재 값
   */
  current: number;

  /**
   * 최대 값
   */
  max: number;

  /**
   * 라벨 (HP, PROGRESS 등)
   */
  label?: string;

  /**
   * 색상 테마
   * - health: HP 바 (초록 → 노랑 → 빨강)
   * - progress: 진행도 바 (고정 색상)
   */
  theme?: 'health' | 'progress';

  /**
   * 숫자 표시 여부
   */
  showNumbers?: boolean;
}

/**
 * HPBar 컴포넌트
 * 레트로 게임 HUD 스타일의 바
 */
export const HPBar: React.FC<HPBarProps> = ({
  current,
  max,
  label = 'HP',
  theme = 'health',
  showNumbers = true,
}) => {
  // 백분율 계산
  const percentage = Math.min(100, Math.max(0, (current / max) * 100));

  /**
   * HP 비율에 따른 색상 결정 (health 테마만)
   */
  const getBarColor = (): string => {
    if (theme === 'progress') {
      return '#9bbc0f'; // 게임보이 그린 (고정)
    }

    // health 테마: 비율에 따라 색상 변화
    if (percentage > 60) return '#9bbc0f'; // 초록 (게임보이 그린)
    if (percentage > 30) return '#ffd93d'; // 노랑
    return '#ff6b6b'; // 빨강
  };

  /**
   * HP 비율에 따른 애니메이션
   */
  const getAnimation = (): string => {
    if (theme === 'health' && percentage <= 20) {
      return 'animate-pulse'; // 위험 구간: 깜빡임
    }
    return '';
  };

  return (
    <div className="font-pixel">
      {/* 라벨 및 숫자 */}
      <div className="flex justify-between items-center mb-0.5">
        <span className="text-[10px] text-gray-400 tracking-wider">{label}</span>
        {showNumbers && (
          <span className="text-[10px] text-gray-400 font-mono">
            {current}/{max}
          </span>
        )}
      </div>

      {/* HP 바 컨테이너 */}
      <div
        className="bg-gray-900 overflow-hidden"
        style={{
          border: '2px solid #4b5563',
          boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.5)',
          height: '16px',
        }}
      >
        {/* HP 바 내부 (도트 패턴) */}
        <div
          className={`h-full transition-all duration-300 ${getAnimation()}`}
          style={{
            width: `${percentage}%`,
            backgroundColor: getBarColor(),
            boxShadow: `inset 0 0 8px rgba(0,0,0,0.2)`,
            backgroundImage:
              'repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(0,0,0,0.1) 2px, rgba(0,0,0,0.1) 4px)',
          }}
        >
          {/* 내부 하이라이트 (입체감) */}
          <div
            className="h-1/2 w-full"
            style={{
              background:
                'linear-gradient(to bottom, rgba(255,255,255,0.2), transparent)',
            }}
          />
        </div>
      </div>

      {/* 백분율 표시 (선택사항) */}
      {theme === 'progress' && (
        <div className="mt-1 text-right">
          <span className="text-xs text-gray-500">{Math.floor(percentage)}%</span>
        </div>
      )}
    </div>
  );
};
